import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  ArrowLeft, MapPin, Building2, Plus, Trash2, Package, HardHat, Flag,
  Image as ImageIcon, User, AlertTriangle, Mail, Download, Compass,
} from "lucide-react";
import { toast } from "sonner";
import WeatherCard from "@/components/WeatherCard";
import WeeklyReportCard from "@/components/WeeklyReportCard";

const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  planning: "bg-sky-100 text-sky-800 border-sky-200",
  on_hold: "bg-amber-100 text-amber-800 border-amber-200",
  completed: "bg-neutral-100 text-neutral-700 border-neutral-200",
};
const PRIO_META = {
  low: "bg-neutral-100 text-neutral-700 border-neutral-200",
  medium: "bg-sky-100 text-sky-800 border-sky-200",
  high: "bg-amber-100 text-amber-800 border-amber-200",
  critical: "bg-red-100 text-red-800 border-red-200",
};
const SNAG_STATUS_META = {
  open: "bg-amber-100 text-amber-800 border-amber-200",
  in_progress: "bg-sky-100 text-sky-800 border-sky-200",
  resolved: "bg-emerald-100 text-emerald-800 border-emerald-200",
};
const TYPE_META = {
  delivery:      { icon: Package, bar: "bg-[#F5F1E6]0" },
  subcontractor: { icon: HardHat, bar: "bg-sky-500" },
  milestone:     { icon: Flag,    bar: "bg-emerald-500" },
};

function AuthedImage({ path }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url; let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/files/${path}`, { responseType: "blob" });
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setSrc(url);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!src) return <div className="w-full aspect-video bg-neutral-100 animate-pulse rounded-md" />;
  return <img src={src} alt="snag" className="w-full aspect-video object-cover rounded-md border border-neutral-200" />;
}

export default function ProjectDetailPage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const currentTab = params.get("tab") || "overview";

  const [project, setProject] = useState(null);
  const [events, setEvents] = useState([]);
  const [snags, setSnags] = useState([]);
  const [selectedSnagId, setSelectedSnagId] = useState(params.get("snag") || null);
  const [loading, setLoading] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [snagOpen, setSnagOpen] = useState(false);
  const [eventOpen, setEventOpen] = useState(false);

  const [managersInput, setManagersInput] = useState("");
  const [projForm, setProjForm] = useState({});

  const [snagForm, setSnagForm] = useState({
    title: "", description: "", location: "", priority: "medium",
    status: "open", assigned_to: "", photo_path: null, photo_url: null,
  });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const [eventForm, setEventForm] = useState({
    title: "", type: "delivery", start_date: "", end_date: "",
    contractor: "", notes: "", status: "scheduled",
  });

  const [aiModel, setAiModel] = useState("claude-sonnet-4-5");
  const [aiLoading, setAiLoading] = useState(null); // deprecated — AI assistant removed

  const load = async () => {
    setLoading(true);
    try {
      const [proj, ev, sn] = await Promise.all([
        api.get(`/projects/${id}?lang=${lang}`),
        api.get(`/timeline?project_id=${id}&lang=${lang}`),
        api.get(`/snags?project_id=${id}&lang=${lang}`),
      ]);
      setProject(proj.data);
      setEvents(ev.data);
      setSnags(sn.data);
      setManagersInput((proj.data.site_managers || []).join(", "));
      setProjForm({
        name: proj.data.name || "", client: proj.data.client || "", address: proj.data.address || "",
        status: proj.data.status, start_date: proj.data.start_date || "", end_date: proj.data.end_date || "",
        progress: proj.data.progress || 0, budget: proj.data.budget || 0, description: proj.data.description || "",
        latitude: proj.data.latitude ?? "", longitude: proj.data.longitude ?? "",
      });
      if (!selectedSnagId && sn.data[0]) setSelectedSnagId(sn.data[0].id);
    } catch (e) {
      toast.error(apiErr(e));
      if (e.response?.status === 404) nav("/app/projects");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, lang]);

  const stats = useMemo(() => ({
    total_snags: snags.length,
    open_snags: snags.filter((s) => s.status === "open").length,
    critical_snags: snags.filter((s) => s.priority === "critical").length,
    events: events.length,
  }), [snags, events]);

  const setTab = (v) => {
    const p = new URLSearchParams(params);
    p.set("tab", v);
    setParams(p, { replace: true });
  };

  const saveProject = async () => {
    try {
      const managers = managersInput.split(",").map((s) => s.trim()).filter((s) => s.includes("@"));
      const latitude = projForm.latitude === "" || projForm.latitude === null ? null : Number(projForm.latitude);
      const longitude = projForm.longitude === "" || projForm.longitude === null ? null : Number(projForm.longitude);
      await api.patch(`/projects/${id}`, {
        ...projForm,
        progress: Number(projForm.progress), budget: Number(projForm.budget),
        site_managers: managers,
        latitude, longitude,
      });
      setEditOpen(false);
      await load();
      toast.success(t("saved"));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const detectLocation = async () => {
    const q = (projForm.address || "").trim();
    if (!q) return toast.error(t("geocode_need_address"));
    try {
      const { data } = await api.get(`/geocode?q=${encodeURIComponent(q)}`);
      if (!data.length) return toast.error(t("geocode_none"));
      const first = data[0];
      setProjForm((f) => ({ ...f, latitude: first.latitude, longitude: first.longitude }));
      toast.success(`${first.name}${first.country ? ", " + first.country : ""}`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const downloadPdf = () => {
    const token = localStorage.getItem("obra_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/projects/${id}/snag-report.pdf?lang=${lang}&auth=${encodeURIComponent(token || "")}`;
    window.open(url, "_blank", "noopener");
  };

  const removeProject = async () => {
    if (!confirm(t("delete_project_confirm"))) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success(t("deleted"));
      nav("/app/projects");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const uploadPhoto = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSnagForm((f) => ({ ...f, photo_path: data.path, photo_url: data.url }));
      toast.success(t("photo_uploaded"));
    } catch (e) { toast.error(apiErr(e)); }
    finally { setUploading(false); }
  };

  const createSnag = async () => {
    if (!snagForm.title) return toast.error(t("title_required"));
    try {
      const { data } = await api.post("/snags", { project_id: id, ...snagForm });
      setSnags((prev) => [data, ...prev]);
      setSelectedSnagId(data.id);
      setSnagOpen(false);
      setSnagForm({
        title: "", description: "", location: "", priority: "medium",
        status: "open", assigned_to: "", photo_path: null, photo_url: null,
      });
      if (data.priority === "critical") {
        toast.success(t("critical_notified"));
        window.dispatchEvent(new Event("obra:notif-refresh"));
      } else {
        toast.success(t("snag_created"));
      }
    } catch (e) { toast.error(apiErr(e)); }
  };

  const updateSnag = async (sid, patch) => {
    try {
      const { data } = await api.patch(`/snags/${sid}`, patch);
      setSnags((prev) => prev.map((s) => (s.id === sid ? data : s)));
      if (patch.priority === "critical") {
        toast.success(t("critical_notified"));
        window.dispatchEvent(new Event("obra:notif-refresh"));
      }
    } catch (e) { toast.error(apiErr(e)); }
  };

  const deleteSnag = async (sid) => {
    if (!confirm(t("delete_snag_confirm"))) return;
    try {
      await api.delete(`/snags/${sid}`);
      setSnags((prev) => prev.filter((s) => s.id !== sid));
      if (selectedSnagId === sid) setSelectedSnagId(null);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const runAI = async () => { /* AI assistant removed */ };

  const createEvent = async () => {
    if (!eventForm.title || !eventForm.start_date) return toast.error(t("fill_event_required"));
    try {
      const { data } = await api.post("/timeline", {
        project_id: id, ...eventForm,
        end_date: eventForm.end_date || eventForm.start_date,
      });
      setEvents((prev) => [...prev, data].sort((a, b) => (a.start_date || "").localeCompare(b.start_date || "")));
      setEventOpen(false);
      setEventForm({
        title: "", type: "delivery", start_date: "", end_date: "",
        contractor: "", notes: "", status: "scheduled",
      });
      toast.success(t("event_added"));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const deleteEvent = async (eid) => {
    if (!confirm(t("delete_event_confirm"))) return;
    try {
      await api.delete(`/timeline/${eid}`);
      setEvents((prev) => prev.filter((e) => e.id !== eid));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const selectedSnag = snags.find((s) => s.id === selectedSnagId) || snags[0] || null;

  if (loading || !project) {
    return <div className="text-neutral-400 text-sm">Loading…</div>;
  }

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      {/* Header */}
      <div>
        <Link to="/app/projects" className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-[#B8933A] transition-colors duration-200 mb-3" data-testid="back-to-projects">
          <ArrowLeft className="w-4 h-4" />{t("all_projects")}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge className={`border ${STATUS_COLORS[project.status] || ""}`}>{t(project.status)}</Badge>
              {stats.critical_snags > 0 && (
                <Badge className="border bg-red-100 text-red-800 border-red-200" data-testid="critical-badge">
                  <AlertTriangle className="w-3 h-3 mr-1" />{stats.critical_snags} {t("critical")}
                </Badge>
              )}
            </div>
            <h1 className="mt-2 font-[Fraunces] text-4xl font-bold tracking-tight text-neutral-950" data-testid="project-detail-name">
              {project.name}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-neutral-500">
              {project.client && (<span className="flex items-center gap-1.5"><Building2 className="w-4 h-4" />{project.client}</span>)}
              {project.address && (<span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{project.address}</span>)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={downloadPdf} data-testid="download-pdf-button">
              <Download className="w-4 h-4 mr-1" />{t("export_pdf")}
            </Button>
            <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-project-button">{t("edit")}</Button>
            <Button variant="outline" onClick={removeProject} className="text-red-700 border-red-200 hover:bg-red-50" data-testid="delete-project-button">
              <Trash2 className="w-4 h-4 mr-1" />{t("delete")}
            </Button>
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="surface-card p-5"><p className="label-eyebrow">{t("progress")}</p>
          <p className="font-[Fraunces] text-3xl font-bold mt-1">{project.progress}%</p>
          <Progress value={project.progress} className="h-1.5 mt-3" />
        </div>
        <div className="surface-card p-5"><p className="label-eyebrow">{t("total_snags")}</p>
          <p className="font-[Fraunces] text-3xl font-bold mt-1">{stats.total_snags}</p>
          <p className="text-xs text-amber-700 mt-1">{stats.open_snags} {t("open")}</p>
        </div>
        <div className="surface-card p-5"><p className="label-eyebrow">{t("timeline_events")}</p>
          <p className="font-[Fraunces] text-3xl font-bold mt-1">{stats.events}</p>
        </div>
        <div className="surface-card p-5"><p className="label-eyebrow">{t("site_managers")}</p>
          <p className="font-[Fraunces] text-3xl font-bold mt-1">{(project.site_managers || []).length}</p>
          <p className="text-xs text-neutral-500 mt-1 truncate">{(project.site_managers || []).join(", ") || t("none_configured")}</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={currentTab} onValueChange={setTab}>
        <TabsList className="bg-white border border-neutral-200 rounded-md p-1">
          <TabsTrigger value="overview" data-testid="tab-overview">{t("overview")}</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">{t("timeline")} ({stats.events})</TabsTrigger>
          <TabsTrigger value="snags" data-testid="tab-snags">{t("snags")} ({stats.total_snags})</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="surface-card p-6">
                <p className="label-eyebrow mb-3">{t("description")}</p>
                <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap">
                  {project.description || t("no_description")}
                </p>
              </div>
              <WeatherCard
                projectId={id}
                hasLocation={project.latitude != null && project.longitude != null}
                onSetLocation={() => setEditOpen(true)}
              />
              <WeeklyReportCard projectId={id} />
            </div>
            <div className="surface-card p-6 space-y-4">
              <div>
                <p className="label-eyebrow">{t("start_date")}</p>
                <p className="text-sm text-neutral-900 mt-1">{project.start_date || "—"}</p>
              </div>
              <div>
                <p className="label-eyebrow">{t("end_date")}</p>
                <p className="text-sm text-neutral-900 mt-1">{project.end_date || "—"}</p>
              </div>
              <div>
                <p className="label-eyebrow">{t("budget")}</p>
                <p className="font-[Fraunces] text-lg text-neutral-950 mt-1">
                  {project.budget ? `€${Number(project.budget).toLocaleString()}` : "—"}
                </p>
              </div>
              <div>
                <p className="label-eyebrow flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{t("site_managers")}</p>
                {(project.site_managers || []).length ? (
                  <ul className="mt-2 space-y-1">
                    {project.site_managers.map((m) => (
                      <li key={m} className="text-sm text-neutral-900 bg-neutral-50 border border-neutral-200 rounded px-2 py-1">{m}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-neutral-500 mt-1">{t("no_managers_hint")}</p>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* TIMELINE */}
        <TabsContent value="timeline" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={eventOpen} onOpenChange={setEventOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#B8933A] hover:bg-[#8C6D22]" data-testid="detail-new-event-button">
                  <Plus className="w-4 h-4 mr-1" />{t("new_event")}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle className="font-[Fraunces]">{t("new_event")}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label className="label-eyebrow">{t("event_title")}</Label>
                    <Input value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} data-testid="detail-event-title-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-eyebrow">{t("type")}</Label>
                    <Select value={eventForm.type} onValueChange={(v) => setEventForm({ ...eventForm, type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="delivery">{t("delivery")}</SelectItem>
                        <SelectItem value="subcontractor">{t("subcontractor")}</SelectItem>
                        <SelectItem value="milestone">{t("milestone")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-eyebrow">{t("contractor")}</Label>
                    <Input value={eventForm.contractor} onChange={(e) => setEventForm({ ...eventForm, contractor: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-eyebrow">{t("start_date")}</Label>
                    <Input type="date" value={eventForm.start_date} onChange={(e) => setEventForm({ ...eventForm, start_date: e.target.value })} data-testid="detail-event-start-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-eyebrow">{t("end_date")}</Label>
                    <Input type="date" value={eventForm.end_date} onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })} />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label className="label-eyebrow">{t("notes")}</Label>
                    <Textarea rows={2} value={eventForm.notes} onChange={(e) => setEventForm({ ...eventForm, notes: e.target.value })} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEventOpen(false)}>{t("cancel")}</Button>
                  <Button className="bg-[#B8933A] hover:bg-[#8C6D22]" onClick={createEvent} data-testid="detail-save-event-button">{t("save")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {events.length === 0 ? (
            <div className="surface-card p-12 text-center text-sm text-neutral-500">{t("no_events")}</div>
          ) : (
            <div className="surface-card divide-y divide-neutral-100">
              {events.map((e) => {
                const meta = TYPE_META[e.type] || TYPE_META.delivery;
                const Icon = meta.icon;
                return (
                  <div key={e.id} data-testid={`detail-event-${e.id}`}
                    className="p-4 flex items-center gap-4 hover:bg-neutral-50 transition-colors duration-200">
                    <div className={`w-9 h-9 rounded-md ${meta.bar} text-white flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-neutral-950 truncate">{e.title}</p>
                        <Badge variant="outline" className="text-[10px]">{t(e.type)}</Badge>
                      </div>
                      <p className="text-xs text-neutral-500 mt-0.5">
                        {e.start_date}{e.end_date && e.end_date !== e.start_date ? ` → ${e.end_date}` : ""}
                        {e.contractor ? ` · ${e.contractor}` : ""}
                      </p>
                    </div>
                    <button onClick={() => deleteEvent(e.id)}
                      className="text-neutral-400 hover:text-red-600 transition-colors duration-200"
                      data-testid={`delete-event-${e.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* SNAGS */}
        <TabsContent value="snags" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={snagOpen} onOpenChange={setSnagOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#B8933A] hover:bg-[#8C6D22]" data-testid="detail-new-snag-button">
                  <Plus className="w-4 h-4 mr-1" />{t("new_snag")}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle className="font-[Fraunces]">{t("new_snag")}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label className="label-eyebrow">{t("snag_title")}</Label>
                    <Input value={snagForm.title} onChange={(e) => setSnagForm({ ...snagForm, title: e.target.value })} data-testid="detail-snag-title-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-eyebrow">{t("priority")}</Label>
                    <Select value={snagForm.priority} onValueChange={(v) => setSnagForm({ ...snagForm, priority: v })}>
                      <SelectTrigger data-testid="detail-snag-priority-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">{t("low")}</SelectItem>
                        <SelectItem value="medium">{t("medium")}</SelectItem>
                        <SelectItem value="high">{t("high")}</SelectItem>
                        <SelectItem value="critical">{t("critical")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-eyebrow">{t("location")}</Label>
                    <Input value={snagForm.location} onChange={(e) => setSnagForm({ ...snagForm, location: e.target.value })} />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label className="label-eyebrow">{t("assigned_to")}</Label>
                    <Input value={snagForm.assigned_to} onChange={(e) => setSnagForm({ ...snagForm, assigned_to: e.target.value })} />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label className="label-eyebrow">{t("description")}</Label>
                    <Textarea rows={3} value={snagForm.description} onChange={(e) => setSnagForm({ ...snagForm, description: e.target.value })} />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label className="label-eyebrow">{t("upload_photo")}</Label>
                    <div
                      onClick={() => fileRef.current?.click()}
                      onDragOver={(evt) => evt.preventDefault()}
                      onDrop={(evt) => { evt.preventDefault(); const f = evt.dataTransfer.files?.[0]; if (f) uploadPhoto(f); }}
                      className="border-2 border-dashed border-neutral-300 hover:border-[#8C6D22] rounded-md p-6 text-center cursor-pointer transition-colors duration-200">
                      {uploading ? <p className="text-sm text-neutral-500">Uploading…</p> :
                        snagForm.photo_path ?
                          <p className="text-sm text-emerald-700">Photo attached ✓</p> :
                          <p className="text-sm text-neutral-500 flex items-center justify-center gap-2"><ImageIcon className="w-4 h-4" />{t("drop_photo")}</p>}
                      <input ref={fileRef} type="file" accept="image/*" hidden
                        onChange={(evt) => { const f = evt.target.files?.[0]; if (f) uploadPhoto(f); }} />
                    </div>
                    {snagForm.priority === "critical" && (project.site_managers || []).length > 0 && (
                      <p className="text-xs text-[#B8933A] mt-1 flex items-center gap-1">
                        <Mail className="w-3 h-3" />{t("critical_will_email")}
                      </p>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setSnagOpen(false)}>{t("cancel")}</Button>
                  <Button className="bg-[#B8933A] hover:bg-[#8C6D22]" onClick={createSnag} data-testid="detail-save-snag-button">{t("save")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {snags.length === 0 ? (
            <div className="surface-card p-12 text-center text-sm text-neutral-500">{t("no_snags")}</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-4 space-y-2">
                {snags.map((s) => (
                  <button key={s.id}
                    onClick={() => setSelectedSnagId(s.id)}
                    data-testid={`detail-snag-item-${s.id}`}
                    className={`w-full text-left surface-card p-4 transition-colors duration-200 ${selectedSnag?.id === s.id ? "border-[#B8933A] bg-[#F5F1E6]/40" : "hover:border-neutral-400"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium text-neutral-950 truncate">{s.title}</p>
                      <Badge variant="outline" className={`border ${PRIO_META[s.priority]}`}>{t(s.priority)}</Badge>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <Badge variant="outline" className={`border ${SNAG_STATUS_META[s.status]}`}>{t(s.status)}</Badge>
                      {s.location && <span className="text-neutral-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{s.location}</span>}
                    </div>
                  </button>
                ))}
              </div>
              <div className="lg:col-span-8">
                {selectedSnag ? (
                  <div className="surface-card p-6 space-y-6" data-testid="detail-snag-panel">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <h2 className="font-[Fraunces] font-bold text-2xl text-neutral-950">{selectedSnag.title}</h2>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs">
                          <Badge variant="outline" className={`border ${PRIO_META[selectedSnag.priority]}`}>{t(selectedSnag.priority)}</Badge>
                          <Badge variant="outline" className={`border ${SNAG_STATUS_META[selectedSnag.status]}`}>{t(selectedSnag.status)}</Badge>
                          {selectedSnag.location && <span className="text-neutral-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{selectedSnag.location}</span>}
                          {selectedSnag.assigned_to && <span className="text-neutral-500 flex items-center gap-1"><User className="w-3 h-3" />{selectedSnag.assigned_to}</span>}
                        </div>
                      </div>
                      <button onClick={() => deleteSnag(selectedSnag.id)} className="text-neutral-400 hover:text-red-600 transition-colors duration-200">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {selectedSnag.photo_path && <AuthedImage path={selectedSnag.photo_path} />}
                    {selectedSnag.description && (
                      <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap">{selectedSnag.description}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-3">
                      <Label className="label-eyebrow">{t("status")}</Label>
                      <Select value={selectedSnag.status} onValueChange={(v) => updateSnag(selectedSnag.id, { status: v })}>
                        <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">{t("open")}</SelectItem>
                          <SelectItem value="in_progress">{t("in_progress")}</SelectItem>
                          <SelectItem value="resolved">{t("resolved")}</SelectItem>
                        </SelectContent>
                      </Select>
                      <Label className="label-eyebrow ml-4">{t("priority")}</Label>
                      <Select value={selectedSnag.priority} onValueChange={(v) => updateSnag(selectedSnag.id, { priority: v })}>
                        <SelectTrigger className="w-40" data-testid="detail-priority-update"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">{t("low")}</SelectItem>
                          <SelectItem value="medium">{t("medium")}</SelectItem>
                          <SelectItem value="high">{t("high")}</SelectItem>
                          <SelectItem value="critical">{t("critical")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="border-t border-neutral-200 pt-6" />
                  </div>
                ) : (
                  <div className="surface-card p-12 text-center text-sm text-neutral-500">{t("select_snag_prompt")}</div>
                )}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit project dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-[Fraunces]">{t("edit_project")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2">
              <Label className="label-eyebrow">{t("project_name")}</Label>
              <Input value={projForm.name} onChange={(e) => setProjForm({ ...projForm, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label className="label-eyebrow">{t("client")}</Label>
              <Input value={projForm.client} onChange={(e) => setProjForm({ ...projForm, client: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label className="label-eyebrow">{t("status")}</Label>
              <Select value={projForm.status} onValueChange={(v) => setProjForm({ ...projForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="planning">{t("planning")}</SelectItem>
                  <SelectItem value="active">{t("in_progress")}</SelectItem>
                  <SelectItem value="on_hold">{t("on_hold")}</SelectItem>
                  <SelectItem value="completed">{t("completed")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-2">
              <Label className="label-eyebrow">{t("address")}</Label>
              <Input value={projForm.address} onChange={(e) => setProjForm({ ...projForm, address: e.target.value })} />
            </div>
            <div className="col-span-2 grid grid-cols-[1fr_1fr_auto] gap-3 items-end">
              <div className="space-y-2">
                <Label className="label-eyebrow">{t("latitude")}</Label>
                <Input type="number" step="0.0001" value={projForm.latitude ?? ""}
                  onChange={(e) => setProjForm({ ...projForm, latitude: e.target.value })}
                  data-testid="latitude-input" />
              </div>
              <div className="space-y-2">
                <Label className="label-eyebrow">{t("longitude")}</Label>
                <Input type="number" step="0.0001" value={projForm.longitude ?? ""}
                  onChange={(e) => setProjForm({ ...projForm, longitude: e.target.value })}
                  data-testid="longitude-input" />
              </div>
              <Button variant="outline" onClick={detectLocation} className="h-10" data-testid="detect-location-button">
                <Compass className="w-4 h-4 mr-1" />{t("detect")}
              </Button>
            </div>
            <div className="space-y-2">
              <Label className="label-eyebrow">{t("start_date")}</Label>
              <Input type="date" value={projForm.start_date} onChange={(e) => setProjForm({ ...projForm, start_date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label className="label-eyebrow">{t("end_date")}</Label>
              <Input type="date" value={projForm.end_date} onChange={(e) => setProjForm({ ...projForm, end_date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label className="label-eyebrow">{t("progress")} (%)</Label>
              <Input type="number" min="0" max="100" value={projForm.progress}
                onChange={(e) => setProjForm({ ...projForm, progress: e.target.value })} data-testid="edit-progress-input" />
            </div>
            <div className="space-y-2">
              <Label className="label-eyebrow">{t("budget")}</Label>
              <Input type="number" value={projForm.budget} onChange={(e) => setProjForm({ ...projForm, budget: e.target.value })} />
            </div>
            <div className="col-span-2 space-y-2">
              <Label className="label-eyebrow flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" />{t("site_managers")} <span className="normal-case text-neutral-400 tracking-normal">— {t("comma_separated_emails")}</span>
              </Label>
              <Input placeholder="carlos@obra.com, jefe@construc.es" value={managersInput}
                onChange={(e) => setManagersInput(e.target.value)} data-testid="site-managers-input" />
              <p className="text-xs text-neutral-500">{t("site_managers_hint")}</p>
            </div>
            <div className="col-span-2 space-y-2">
              <Label className="label-eyebrow">{t("description")}</Label>
              <Textarea rows={3} value={projForm.description} onChange={(e) => setProjForm({ ...projForm, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>{t("cancel")}</Button>
            <Button onClick={saveProject} className="bg-[#B8933A] hover:bg-[#8C6D22]" data-testid="save-project-detail-button">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
